/**
 * Router setup with placeholder routes and auth guards
 */

import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores'
import { useBetaStore } from '@/stores/beta'
import {useQueryClient} from '@tanstack/vue-query'
import {collectionPrerequisite,capturePreparationMayRecover} from '@/features/session-capture/collectionPrerequisites'
import { installNavigationContext } from '@/features/navigation/navigation'

const routes: RouteRecordRaw[] = [
  { path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/feedback', redirect: to => ({ path: to.path.replace(/\/feedback\/?$/, ''), query: { ...to.query, section: 'feedback' } }) },
  {path:'/account-email/confirm/:token',component:()=>import('@/features/season-reports/EmailConfirmationPage.vue'),meta:{layout:'public',requiresAuth:false,remountOnPathChange:true}},
  { path: '/reviewoffer/:participantId', name: 'review-offer', component: () => import('@/features/offer-recipient/OfferReviewPage.vue'), meta: { layout: 'public', requiresAuth: true, remountOnPathChange: true } },
  { path: '/safeguarding', name: 'safeguarding', component: () => import('@/app/SafeguardingPage.vue'), meta: { layout: 'public', requiresAuth: false } },
  { path: '/your-safety', name: 'your-safety', component: () => import('@/app/YourSafetyPage.vue'), meta: { layout: 'public', requiresAuth: false } },
  { path: '/coaches/:shareId', component: () => import('@/app/PublicCoachPage.vue'), meta: { layout: 'public', requiresAuth: false } },
  { path: '/terms', name: 'terms', component: () => import('@/app/TermsPage.vue'), meta: { layout: 'public', requiresAuth: false } },
  { path: '/privacy', name: 'privacy', component: () => import('@/app/PrivacyPage.vue'), meta: { layout: 'public', requiresAuth: false } },
  { path: '/capture/resume', name: 'offline-capture-resume', component: () => import('@/features/offline-capture/OfflineCaptureResumePage.vue'), meta: { layout: 'authenticated', requiresAuth: false } },
  { path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/capture/offline', name: 'offline-capture', component: () => import('@/features/offline-capture/OfflineCapturePage.vue'), meta: { layout: 'authenticated', requiresAuth: true, remountOnPathChange: true } },
  { path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/badges', component: () => import('@/features/badges/BadgeCollectionPage.vue'), meta: { layout: 'authenticated', requiresAuth: true, eventView: 'athlete' } },
  { path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/scouting', component: () => import('@/features/scouting/ScoutingPage.vue'), meta: { layout: 'authenticated', requiresAuth: true, eventView: 'shared' } },
  { path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/scouting/migrate', component: () => import('@/features/scouting/ScoutingMigration.vue'), meta: { layout: 'authenticated', requiresAuth: true } },
  { path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/scouting/settings', component: () => import('@/features/scouting/ScoutingSettings.vue'), meta: { layout: 'authenticated', requiresAuth: true } },
  { path: '/parent-connections', component: () => import('@/features/parents/ParentConnectionsPage.vue'), meta: { layout: 'authenticated', requiresAuth: true } },
  { path: '/parents', component: () => import('@/features/parents/ParentHomePage.vue'), meta: { layout: 'authenticated', requiresAuth: true } },
  { path: '/parents/:linkId', component: () => import('@/features/parents/ParentChildPage.vue'), meta: { layout: 'authenticated', requiresAuth: true } },
  {
    path: '/',
    component: () => import('@/app/ClubRoomsPage.vue'),
    alias: '/clubrooms',
    meta: { layout: 'authenticated', requiresAuth: true, title: 'Club Rooms - CoachSight' },
  },
  {
    path: '/login',
    component: () => import('@/app/LoginPage.vue'),
    meta: { layout: 'public', requiresAuth: false, title: 'Login - CoachSight' },
  },
  {
    path: '/resetpassword/:token/:newUser?',
    component: () => import('@/app/ResetPasswordPage.vue'),
    meta: {
      layout: 'public',
      requiresAuth: false,
      title: 'Reset Password - CoachSight',
    },
  },
  {
    path: '/offline',
    component: () => import('@/app/OfflinePage.vue'),
    meta: { layout: 'public', requiresAuth: false, title: 'Offline - CoachSight' },
  },
  {
    path: '/manageuser',
    component: () => import('@/app/ManageUserPage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, title: 'Account Settings - CoachSight' },
  },
  {
    path: '/completeaccount',
    component: () => import('@/app/CompleteAccountPage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, title: 'Complete Account - CoachSight' },
  },
  {
    path: '/clubhouse/:clubId',
    component: () => import('@/app/ClubhousePage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, title: 'Clubhouse - CoachSight' },
  },
  {
    path: '/clubhouse/:clubId/activities-2',
    component: () => import('@/features/activity-library/ActivityLibraryPage.vue'),
    props: { activities2: true },
    meta: { layout: 'authenticated', requiresAuth: true, title: 'Activities 2 - CoachSight' },
  },
  {
    path: '/clubhouse/:clubId/activity-library',
    component: () => import('@/features/activity-library/ActivityLibraryPage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, title: 'Activity Library - CoachSight' },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId',
    component: () => import('@/app/SeasonHomePage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, title: 'Season Home - CoachSight' },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/coach-journal-2',
    component: () => import('@/features/athlete-journal/CoachJournalPage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, journalDesign: 'application' },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/journal-2',
    component: () => import('@/features/athlete-journal/AthleteJournalPage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, eventView: 'athlete', journalDesign: 'application' },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/coach-journal',
    component: () => import('@/features/athlete-journal/CoachJournalPage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/journal',
    component: () => import('@/features/athlete-journal/AthleteJournalPage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, eventView: 'athlete', title: 'My Journal - CoachSight' },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/episodes',
    component: () => import('@/features/episode-library/EpisodeLibraryPage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, title: 'Episode Library - CoachSight' },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/episodes/new',
    component: () => import('@/features/episode-library/NewEpisodePage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, title: 'New Episode - CoachSight' },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/episodes/:episodeId',
    component: () => import('@/features/episode-library/EpisodeWorkspacePage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, title: 'Episode Workspace - CoachSight' },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/program-review',
    component: () => import('@/features/program-review/ProgramReviewRoute.vue'),
    meta: { layout: 'authenticated', requiresAuth: true },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/program-settings',
    component: () => import('@/features/development-program/ProgramSettingsRoute.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, title: 'Development Program Settings - CoachSight' },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/setup/stripe',
    component: () => import('@/features/tryout-setup/StripeSetupPage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, eventView: 'coach' },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/setup',
    component: () => import('@/features/tryout-setup/TryoutSetupPage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, title: 'Tryout Setup - CoachSight', remountOnPathChange: true },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/import-athletes',
    component: () => import('@/features/participant-import/TryoutParticipantImportPage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, title: 'Import Participants - CoachSight' },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/athletes',
    component: () => import('@/features/participant-list/TryoutParticipantListPage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, title: 'Participant List - CoachSight' },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/decision-room',
    component: () => import('@/features/decision-room/DecisionRoomPage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, title: 'Decision Room - CoachSight', remountOnPathChange: true },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/session-planner',
    component: () => import('@/features/session-planner/SessionPlannerIndexPage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, title: 'Session Planner - CoachSight', remountOnPathChange: true },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/sessions/:sessionId/planner',
    component: () => import('@/features/session-planner/SessionPlannerPage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, title: 'Session Planner - CoachSight', remountOnPathChange: true },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId/capture',
    name: 'session-capture',
    beforeEnter: async to=>{
      const client=useQueryClient(),{captureWorkspaceQueryKey}=await import('@/features/session-capture/composables')
      const club=String(to.params.clubId),season=String(to.params.seasonId),event=String(to.params.tryoutId),actor=useAuthStore().user?.id||'signed-out'
      const token=useAuthStore().token
      const sameAccount=()=>useAuthStore().token===token && useAuthStore().user?.id===actor
      const queryKey=captureWorkspaceQueryKey(club,season,event,actor)
      try{
        const data=await client.fetchQuery({queryKey,queryFn:async({signal})=>(await import('@/api/sessionCapture')).sessionCaptureAPI.loadWorkspace(season,event,club,signal),staleTime:0,networkMode:'always',retry:false})
        if(sameAccount() && !collectionPrerequisite(data.sessions,data.participants))return true
      }catch(error){
        if(sameAccount() && capturePreparationMayRecover(error)) {
          const {usePreparedSessions}=await import('@/features/offline-capture/preparedSessions')
          if(await usePreparedSessions().canRestoreCapture(club,season,event,String(to.query.sessionId || '')) && sameAccount())return true
        }
        // Denied access and missing prerequisites still belong to the home workspace.
      }
      return {path:`/clubhouse/${club}/season/${season}/tryout/${event}`,query:{section:'assessment'},replace:true}
    },
    component: () => import('@/features/session-capture/SessionCapturePage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, title: 'Session Capture - CoachSight' },
  },
  {
    path: '/clubhouse/:clubId/season/:seasonId/tryout/:tryoutId',
    component: () => import('@/features/tryout-home/TryoutHomePage.vue'),
    meta: { layout: 'authenticated', requiresAuth: true, title: 'Tryout Home - CoachSight' },
  },
  {
    path: '/check-in/:eventId',
    component: () => import('@/features/session-checkin/SessionCheckInPage.vue'),
    meta: { layout: 'public', requiresAuth: false, title: 'Event Check-In - CoachSight' },
  },
  {
    path: '/checkin/:sessionId/:eventId',
    alias: '/signinforsession/:sessionId/:eventId',
    component: () => import('@/features/session-checkin/SessionCheckInPage.vue'),
    meta: {
      layout: 'public',
      requiresAuth: false,
      title: 'Session Check-In - CoachSight',
    },
  },
  {
    path: '/register',
    component: () => import('@/features/tryout-registration/PublicCodeEntryPage.vue'),
    props: { kind: 'registration' },
    meta: { layout: 'public', requiresAuth: false, title: 'Event Registration - CoachSight' },
  },
  {
    path: '/checkin',
    component: () => import('@/features/tryout-registration/PublicCodeEntryPage.vue'),
    props: { kind: 'checkin' },
    meta: { layout: 'public', requiresAuth: false, title: 'Session Check-In - CoachSight' },
  },
  {
    path: '/:code([2-9A-HJ-NP-Z]{3}-?[2-9A-HJ-NP-Z]{3})',
    component: () => import('@/features/tryout-registration/PublicShortcutPage.vue'),
    meta: { layout: 'public', requiresAuth: false, title: 'CoachSight' },
  },
  {
    path: '/registerfortryout/:tryoutId',
    component: () => import('@/features/tryout-registration/TryoutRegistrationPage.vue'),
    meta: {
      layout: 'public',
      requiresAuth: false,
      title: 'Tryout Registration - CoachSight',
    },
  },
  {
    path: '/registerfortryout/:tryoutId/success',
    component: () => import('@/features/tryout-registration/TryoutRegistrationPage.vue'),
    meta: {
      layout: 'public',
      requiresAuth: false,
      title: 'Tryout Registration Success - CoachSight',
    },
  },
  {
    path: '/stripe/onboard/success',
    component: () => import('@/app/StripeResponsePage.vue'),
    meta: {
      layout: 'public',
      requiresAuth: false,
      title: 'Stripe Onboarding Complete - CoachSight',
    },
  },
  {
    path: '/stripe/onboard/refresh',
    component: () => import('@/app/StripeResponsePage.vue'),
    meta: {
      layout: 'public',
      requiresAuth: false,
      title: 'Stripe Onboarding Refresh - CoachSight',
    },
  },
  {
    path: '/stripe/tryoutcharge/success/:token/:type',
    component: () => import('@/features/tryout-registration/TryoutChargeSuccessPage.vue'),
    meta: {
      layout: 'public',
      requiresAuth: false,
      title: 'Tryout Registration Success - CoachSight',
    },
  },
  {
    path: '/stripe/tryoutcharge/failure',
    component: () => import('@/app/StripeResponsePage.vue'),
    meta: {
      layout: 'public',
      requiresAuth: false,
      title: 'Tryout Payment Failed - CoachSight',
    },
  },
  {
    path: '/stripe/tryoutcharge/cancelled',
    alias: '/stripe/tryoutcharge/cancel',
    component: () => import('@/app/StripeResponsePage.vue'),
    meta: {
      layout: 'public',
      requiresAuth: false,
      title: 'Tryout Payment Cancelled - CoachSight',
    },
  },
  {
    path: '/stripe/offercharge/success/:token/:type',
    component: () => import('@/app/StripeResponsePage.vue'),
    meta: {
      layout: 'public',
      requiresAuth: false,
      title: 'Offer Payment Success - CoachSight',
    },
  },
  {
    path: '/stripe/offercharge/failure',
    component: () => import('@/app/StripeResponsePage.vue'),
    meta: {
      layout: 'public',
      requiresAuth: false,
      title: 'Offer Payment Failed - CoachSight',
    },
  },
  {
    path: '/stripe/offercharge/cancelled',
    component: () => import('@/app/StripeResponsePage.vue'),
    meta: {
      layout: 'public',
      requiresAuth: false,
      title: 'Offer Payment Cancelled - CoachSight',
    },
  },
  {
    path: '/stripe/packagecharge/success/:token/:type',
    component: () => import('@/app/StripeResponsePage.vue'),
    meta: {
      layout: 'public',
      requiresAuth: false,
      title: 'Package Payment Success - CoachSight',
    },
  },
  {
    path: '/stripe/packagecharge/failure/:token?/:type?',
    component: () => import('@/app/StripeResponsePage.vue'),
    meta: {
      layout: 'public',
      requiresAuth: false,
      title: 'Package Payment Failed - CoachSight',
    },
  },
  {
    path: '/stripe/packagecharge/cancelled/:token?/:type?',
    component: () => import('@/app/StripeResponsePage.vue'),
    meta: {
      layout: 'public',
      requiresAuth: false,
      title: 'Package Payment Cancelled - CoachSight',
    },
  },
  {
    path: '/sysadmin/analytics',
    component: () => import('@/app/SystemAnalyticsPage.vue'),
    meta: {
      layout: 'authenticated',
      requiresAuth: true,
      requiresSystemAdmin: true,
      title: 'System Analytics - CoachSight',
    },
  },
  {
    path: '/sysadmin/dashboard',
    component: () => import('@/app/SystemInformationPage.vue'),
    meta: {
      layout: 'authenticated',
      requiresAuth: true,
      requiresSystemAdmin: true,
      title: 'System Information - CoachSight',
    },
  },
  {
    path: '/sysadmin/platform-maintenance',
    component: () => import('@/app/PlatformMaintenancePage.vue'),
    meta: {
      layout: 'authenticated',
      requiresAuth: true,
      requiresSystemAdmin: true,
      title: 'Platform Maintenance - CoachSight',
    },
  },
  {
    path: '/404',
    component: () => import('@/app/NotFoundPage.vue'),
    meta: { layout: 'public', title: 'Not Found - CoachSight' },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404',
  },
]

if (import.meta.env.DEV) {
  routes.push({
    path: '/dev/journal-fixtures',
    component: () => import('@/features/athlete-journal/journal/JournalFixtureGallery.vue'),
    meta: { layout: 'public', requiresAuth: false },
  })
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

installNavigationContext(router)

// Navigation guard: Check auth and redirect appropriately
router.beforeEach((to, _from, next) => {
  // Installed starts and cached SPA home navigation need a local recovery entry.
  if (useBetaStore().enabled && !navigator.onLine && (to.path === '/' || to.path === '/clubrooms')) {
    next({ name: 'offline-capture-resume', replace: true }); return
  }
  const authStore = useAuthStore()
  if (!useBetaStore().enabled && ['offline-capture','offline-capture-resume'].includes(String(to.name))) {
    next(to.name === 'offline-capture' ? {path:`/clubhouse/${to.params.clubId}/season/${to.params.seasonId}/tryout/${to.params.tryoutId}/capture`,query:to.query,replace:true} : {path:'/',replace:true}); return
  }
  const requiresAuth = to.meta.requiresAuth as boolean | undefined
  const requiresSystemAdmin = to.meta.requiresSystemAdmin as boolean | undefined

  // If route requires auth and user is not authenticated
  if (requiresAuth && !authStore.isAuthenticated) {
    sessionStorage.setItem('redirectPath', to.fullPath)
    next('/login')
    return
  }

  if (requiresSystemAdmin && authStore.user?.details?.systemAdmin !== true) {
    next('/')
    return
  }

  if (
    requiresAuth &&
    authStore.user?.details?.updateDetails === true &&
    to.path !== '/completeaccount'
  ) {
    next('/completeaccount')
    return
  }

  // If user is trying to access login but is already authenticated
  if (to.path === '/login' && authStore.isAuthenticated) {
    next('/')
    return
  }

  // Update page title
  const title = to.meta.title as string | undefined
  if (title) {
    document.title = title
  }

  next()
})

export default router
